/**
 * Phase 6: Energy guidance card — permission (early + spill) and preservation.
 * Gated by readiness. When systemRiskState is action_required, forecast overrides permissive messaging.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sun, BatteryFull, AlertTriangle, Minus } from 'lucide-react';
import type { EnergyGuidanceResult } from '@/hooks/useEnergyGuidance';
import type { ReadinessState } from '@/lib/readiness';
import type { DashboardForecastViewModel } from '@/lib/dashboard-forecast';
import { guidanceOverrideCopy } from '@/lib/inline-copy';

function confidenceLabel(c: 'low' | 'medium' | 'high'): string {
  if (c === 'high') return 'High confidence';
  if (c === 'medium') return 'Medium confidence';
  return 'Low confidence';
}

export function EnergyGuidanceCard({
  data,
  loading,
  error,
  readinessState = 'ready',
  systemRiskState = 'ok',
  forecastViewModel,
}: {
  data: EnergyGuidanceResult | null | undefined;
  loading: boolean;
  error: Error | null;
  readinessState?: ReadinessState;
  /** action_required = warnings override; watch = cautious framing; ok = permissive allowed. */
  systemRiskState?: 'ok' | 'watch' | 'action_required';
  /** Shared forecast view model. When provided, displayed values align with OvernightForecastCard + EnergyOutlook. */
  forecastViewModel?: DashboardForecastViewModel | null;
}) {
  if (readinessState === 'connecting' || readinessState === 'warming_up' || readinessState === 'degraded') {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Sun className="h-4 w-4 text-wt-text-secondary" />
            Energy guidance
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-wt-text-secondary">
            {readinessState === 'connecting'
              ? 'Guidance available after system connects.'
              : readinessState === 'warming_up'
                ? 'Guidance available after baseline data is collected.'
                : 'Guidance unavailable while data is outdated.'}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Sun className="h-4 w-4 text-wt-text-secondary" />
            Energy guidance
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 text-secondary text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Evaluating…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Sun className="h-4 w-4 text-wt-text-secondary" />
            Energy guidance
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-secondary">Guidance unavailable. Try again later.</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Sun className="h-4 w-4 text-wt-text-secondary" />
            Energy guidance
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-secondary">No guidance yet.</p>
        </CardContent>
      </Card>
    );
  }

  // Arbitration: action_required = warnings only; watch = cautious framing (no permissive); ok = guidance as-is
  const isPermissiveOrNeutral =
    data.state === 'permission_early' || data.state === 'permission_spill' || data.state === 'neutral';
  const overrideActionRequired =
    systemRiskState === 'action_required' && isPermissiveOrNeutral;
  const overrideWatch =
    systemRiskState === 'watch' && isPermissiveOrNeutral;

  const displayMessage = overrideActionRequired
    ? guidanceOverrideCopy('action_required', {
        energyRemainingKwh: data.forecast_lower_kwh != null ? undefined : undefined,
        reserveKwh: data.reserve_kwh ?? undefined,
        confidence: data.confidence,
      })
    : overrideWatch
      ? guidanceOverrideCopy('watch', {
          reserveKwh: data.reserve_kwh ?? undefined,
          confidence: data.confidence,
        })
      : data.message;
  const Icon =
    overrideActionRequired
      ? AlertTriangle
      : overrideWatch
        ? AlertTriangle
        : data.state === 'permission_early' || data.state === 'permission_spill'
          ? BatteryFull
          : data.state === 'preservation'
            ? AlertTriangle
            : Minus;
  const iconColor =
    overrideActionRequired
      ? 'text-wt-amber-primary'
      : overrideWatch
        ? 'text-wt-amber-primary'
        : data.state === 'permission_early' || data.state === 'permission_spill'
          ? 'text-wt-green-primary'
          : data.state === 'preservation'
            ? 'text-wt-amber-primary'
            : 'text-wt-text-secondary';

  const confidenceClass =
    data.confidence === 'high'
      ? 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20'
      : data.confidence === 'medium'
        ? 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20'
        : 'bg-muted/50 text-wt-text-secondary border-border';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
          <Icon className={`h-4 w-4 ${iconColor}`} />
          Energy guidance
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <p className="text-sm text-foreground">{displayMessage}</p>
        {!overrideActionRequired && !overrideWatch && data.state === 'permission_early' && data.explanation && (
          <p className="text-sm text-wt-text-secondary">{data.explanation}</p>
        )}
        {(data.forecast_lower_kwh != null || data.reserve_kwh != null) && !overrideActionRequired && !overrideWatch && (
          <p className="text-sm text-wt-text-secondary">
            {(() => {
              // Use shared forecast view model for displayed values (same source as OvernightForecastCard + EnergyOutlook).
              const displaySunriseKwh = forecastViewModel && forecastViewModel.source !== 'unavailable'
                ? forecastViewModel.worstAtSunriseKwh
                : data.forecast_lower_kwh;
              const displayReserveKwh = forecastViewModel
                ? forecastViewModel.reserveThresholdKwh
                : data.reserve_kwh;
              return (
                <>
                  {displaySunriseKwh != null && (
                    <>Worst-case at sunrise: <span className="font-mono-numeric">{displaySunriseKwh.toFixed(1)}</span> kWh</>
                  )}
                  {displaySunriseKwh != null && displayReserveKwh != null && ' · '}
                  {displayReserveKwh != null && (
                    <>Reserve: <span className="font-mono-numeric">{displayReserveKwh.toFixed(1)}</span> kWh</>
                  )}
                </>
              );
            })()}
          </p>
        )}
        <Badge variant="outline" className={`text-sm ${confidenceClass}`}>
          {confidenceLabel(data.confidence)}
        </Badge>
      </CardContent>
    </Card>
  );
}
